import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { Settings, Save, Building2, DollarSign, Clock, Bell, QrCode, Database, MessageSquare } from 'lucide-react';

const GeneralSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    beneficiary_name: '',
    beneficiary_address: '',
    currency: 'LEI',
    language: 'ro',
    use_consumable_stock: true,
    allow_price_override: false,
    hours_new_material_request_late: 24,
    hours_new_ticket_late: 24,
    hours_accepted_ticket_late: 48,
    log_retention_years: 5,
    sensor_avg_retention_years: 3,
    sms_enabled: false,
    max_video_upload_mb: 32,
    qr_code_fields: ['name', 'code', 'serial_number'],
    qr_base_url: '',
    public_reporting_enabled: true,
    require_phone_for_notification: false,
    associate_locations_with_nomenclators: false
  });

  const qrFieldOptions = [
    { value: 'name', label: 'Nume echipament' },
    { value: 'code', label: 'Cod echipament' },
    { value: 'serial_number', label: 'Numar serie' },
    { value: 'manufacturer', label: 'Producator' },
    { value: 'model', label: 'Model' },
    { value: 'location', label: 'Locatie' },
    { value: 'category', label: 'Categorie' }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/general-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Use defaults if not found
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/general-settings', settings);
      toast.success('Setari salvate cu succes!');
    } catch (error) {
      toast.error('Eroare la salvarea setarilor');
    } finally {
      setSaving(false);
    }
  };

  const toggleQrField = (field) => {
    if (settings.qr_code_fields.includes(field)) {
      setSettings({
        ...settings,
        qr_code_fields: settings.qr_code_fields.filter(f => f !== field)
      });
    } else {
      setSettings({
        ...settings,
        qr_code_fields: [...settings.qr_code_fields, field]
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="general-settings-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Setari Generale</h1>
          <p className="text-slate-600 mt-1">Configureaza setarile globale ale aplicatiei</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary h-12" data-testid="save-settings-btn">
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'SE SALVEAZA...' : 'SALVEAZA'}
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Company Info */}
        <Card className="card-industrial">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#0052CC]" />
              </div>
              <div>
                <CardTitle className="text-lg">Informatii Beneficiar</CardTitle>
                <CardDescription>Date companie pentru rapoarte si fise de interventie</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nume beneficiar</Label>
                <Input
                  value={settings.beneficiary_name}
                  onChange={(e) => setSettings({ ...settings, beneficiary_name: e.target.value })}
                  placeholder="SC Companie SRL"
                  className="h-11"
                  data-testid="beneficiary-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Adresa beneficiar</Label>
                <Input
                  value={settings.beneficiary_address}
                  onChange={(e) => setSettings({ ...settings, beneficiary_address: e.target.value })}
                  placeholder="Str. Industriala nr. 1, Bucuresti"
                  className="h-11"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial Settings */}
        <Card className="card-industrial">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Setari Financiare</CardTitle>
                <CardDescription>Configurare moneda si stocuri</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Moneda</Label>
                <Select
                  value={settings.currency}
                  onValueChange={(value) => setSettings({ ...settings, currency: value })}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LEI">LEI (RON)</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Utilizeaza stoc consumabile</Label>
                  <p className="text-sm text-slate-500">Stocul consumabilelor va fi utilizat in zonele aplicatiei</p>
                </div>
                <Switch
                  checked={settings.use_consumable_stock}
                  onCheckedChange={(checked) => setSettings({ ...settings, use_consumable_stock: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Permite suprascrierea preturilor</Label>
                  <p className="text-sm text-slate-500">Preturile consumabilelor pot fi editate la revizie/interventie</p>
                </div>
                <Switch
                  checked={settings.allow_price_override}
                  onCheckedChange={(checked) => setSettings({ ...settings, allow_price_override: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Asociaza locatiile cu nomenclatoarele</Label>
                  <p className="text-sm text-slate-500">Preferintele si nomenclatoarele sunt asociate cu locatii specifice</p>
                </div>
                <Switch
                  checked={settings.associate_locations_with_nomenclators}
                  onCheckedChange={(checked) => setSettings({ ...settings, associate_locations_with_nomenclators: checked })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delay Settings */}
        <Card className="card-industrial">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Setari Intarzieri</CardTitle>
                <CardDescription>Numarul de ore dupa care elementele sunt considerate intarziate</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Cerere materiale noua</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={settings.hours_new_material_request_late}
                    onChange={(e) => setSettings({ ...settings, hours_new_material_request_late: parseInt(e.target.value) || 0 })}
                    className="h-11"
                    min="1"
                  />
                  <span className="text-sm text-slate-500">ore</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tichet nou (neacceptat)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={settings.hours_new_ticket_late}
                    onChange={(e) => setSettings({ ...settings, hours_new_ticket_late: parseInt(e.target.value) || 0 })}
                    className="h-11"
                    min="1"
                  />
                  <span className="text-sm text-slate-500">ore</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tichet acceptat</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={settings.hours_accepted_ticket_late}
                    onChange={(e) => setSettings({ ...settings, hours_accepted_ticket_late: parseInt(e.target.value) || 0 })}
                    className="h-11"
                    min="1"
                  />
                  <span className="text-sm text-slate-500">ore</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Storage Settings */}
        <Card className="card-industrial">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Database className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Setari Stocare</CardTitle>
                <CardDescription>Configurare retentie date si limite upload</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Retentie log (ani)</Label>
                <Input
                  type="number"
                  value={settings.log_retention_years}
                  onChange={(e) => setSettings({ ...settings, log_retention_years: parseInt(e.target.value) || 1 })}
                  className="h-11"
                  min="1"
                  max="10"
                />
                <p className="text-xs text-slate-500">Log-urile mai vechi vor fi sterse automat</p>
              </div>
              <div className="space-y-2">
                <Label>Retentie valori senzori (ani)</Label>
                <Input
                  type="number"
                  value={settings.sensor_avg_retention_years}
                  onChange={(e) => setSettings({ ...settings, sensor_avg_retention_years: parseInt(e.target.value) || 1 })}
                  className="h-11"
                  min="1"
                  max="10"
                />
                <p className="text-xs text-slate-500">Valorile medii mai vechi vor fi sterse</p>
              </div>
              <div className="space-y-2">
                <Label>Dimensiune maxima video (MB)</Label>
                <Input
                  type="number"
                  value={settings.max_video_upload_mb}
                  onChange={(e) => setSettings({ ...settings, max_video_upload_mb: parseInt(e.target.value) || 32 })}
                  className="h-11"
                  min="1"
                  max="100"
                />
                <p className="text-xs text-slate-500">Pentru video atasat la revizii</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SMS Settings */}
        <Card className="card-industrial">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Notificari SMS</CardTitle>
                <CardDescription>Activare trimitere SMS din aplicatie</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Label>Activati trimiterea de SMS-uri</Label>
                <p className="text-sm text-slate-500">Necesita activare de la furnizor</p>
              </div>
              <Switch
                checked={settings.sms_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, sms_enabled: checked })}
              />
            </div>
          </CardContent>
        </Card>

        {/* QR Code Settings */}
        <Card className="card-industrial">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <QrCode className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Configurare Cod QR & Raportare Publica</CardTitle>
                <CardDescription>Setari pentru codurile QR si raportare probleme de catre operatori</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* QR Base URL */}
            <div className="space-y-2">
              <Label className="font-medium">URL Baza pentru Coduri QR</Label>
              <Input
                value={settings.qr_base_url}
                onChange={(e) => setSettings({ ...settings, qr_base_url: e.target.value })}
                placeholder={window.location.origin}
                className="h-11 font-mono"
              />
              <p className="text-xs text-slate-500">
                Lasati gol pentru a folosi URL-ul curent al sistemului ({window.location.origin}).
                <br />
                Exemplu: daca aplicatia este accesibila la https://maintix.companie.ro, introduceti acea adresa.
              </p>
              <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">URL generat in QR Code:</p>
                <code className="text-sm text-blue-600">
                  {settings.qr_base_url || window.location.origin}/e/[COD_ECHIPAMENT]
                </code>
              </div>
            </div>

            <Separator />

            {/* Public Reporting Settings */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Raportare Publica (Operatori)</h4>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Activeaza raportarea publica</Label>
                  <p className="text-sm text-slate-500">Operatorii pot raporta probleme scanand codul QR (fara login)</p>
                </div>
                <Switch
                  checked={settings.public_reporting_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, public_reporting_enabled: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Solicita telefon pentru notificare</Label>
                  <p className="text-sm text-slate-500">Operatorii vor fi incurajati sa lase numarul de telefon</p>
                </div>
                <Switch
                  checked={settings.require_phone_for_notification}
                  onCheckedChange={(checked) => setSettings({ ...settings, require_phone_for_notification: checked })}
                />
              </div>
            </div>

            <Separator />

            {/* QR Fields */}
            <div className="space-y-3">
              <Label className="font-medium">Campuri incluse in codul QR (afisaj)</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {qrFieldOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      settings.qr_code_fields.includes(option.value)
                        ? 'border-[#0052CC] bg-blue-50'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={settings.qr_code_fields.includes(option.value)}
                      onChange={() => toggleQrField(option.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GeneralSettingsPage;
